/**
 * State a new requirement.
 *
 * Ordered the way the decision is actually made: pick the level, then what it
 * scopes to, and the ID and the package FOLLOW from those. Both are offered
 * rather than imposed - the suggestion is right the large majority of the
 * time, and editable for the rest.
 *
 * There is no package picker. The package is decided by the same level and
 * scoping that decide the ID, so asking for it separately is asking the same
 * question twice and inviting the two answers to disagree.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FieldControl from './FieldControl'
import ReferencePicker from './ReferencePicker'
import { ApiError, api } from '../../api'
import { useChoices } from '../../hooks/useChoices'
import type { Choices } from '../../hooks/useChoices'
import { useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import { useUnsavedGuard } from '../../state/Unsaved'
import { MONOSPACE } from '../../theme'
import type { KindSchema, Suggestion, TypeChoice } from '../../schema'

type Axis = 'segment' | 'subsystem'

/**
 * The scoping axes a level CARRIES.
 *
 * Both, from L3 down, and that is the model's own arithmetic rather than a
 * preference: L3Requirement and L4Requirement specialize SubsystemScoped, which
 * specializes SegmentScoped, so they carry `segment[1]` AND `subsystem[1]`.
 *
 * This returned ONE axis and asked for only that, so every L3 and L4 created
 * here was written with no segment - and BEST's own findings then reported
 * "L3 requirement has no segment" about a requirement BEST had just made.
 *
 * The LAST one is what files it: the ID and the package follow from the
 * narrowest scoping, which is what services/naming.py suggests against.
 */
function axesFor(level: number | null): Axis[] {
  if (level === 2) return ['segment']
  if (level === 3 || level === 4) return ['segment', 'subsystem']
  return []
}

/** The empty choice, so nothing here has to test for a missing type. */
const NO_TYPE: TypeChoice = { name: '', label: '', level: null, fields: [], template: '' }

/** '{allocated_to}' - one feature slot. Matches metamodel/statements.py. */
const SLOT = /\{([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\}/g

/** The feature names a statement template fills in. */
function slotsIn(template: string): string[] {
  return [...template.matchAll(SLOT)].map((match) => match[1])
}

/**
 * Fields this dialog already asks for, in its own words.
 *
 * A template naming one of these must not produce a second control for it: the
 * level and its scoping decide the ID and the package, so 'subsystem' asked
 * twice is two answers to one question.
 */
const ASKED_ELSEWHERE = new Set(['segment', 'subsystem', 'sources', 'related', 'name',
  'identifier', 'type_name', 'statement', 'status', 'test_level', 'category'])

/** 'thermal' -> 'Thermal', for a package name offered from an enum value. */
function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * One scoping axis: the program's segments, or its subsystems.
 *
 * Says what state its list is in (issue #91). It used to open an EMPTY menu
 * while the list was still on its way, and again for good if the request had
 * failed - on a remote connection that read as a broken dialog, and changing
 * the level "fixed" it only because the answer had arrived by the next click.
 */
function AxisSelect({
  id,
  label,
  plural,
  value,
  onChange,
  choices,
}: {
  id: string
  label: string
  plural: string
  value: string
  onChange: (value: string) => void
  choices: Choices
}) {
  const { options, loading, failed, retry } = choices
  // Said under the field AND in the menu, because the menu is where the person
  // is looking when it has nothing to offer.
  const placeholder = loading
    ? `Loading ${plural}…`
    : failed
      ? `Could not load the ${plural}`
      : options.length === 0
        ? `This program defines no ${plural}`
        : ''

  return (
    <FormControl size="small" required error={failed}>
      <InputLabel id={id}>{label}</InputLabel>
      <Select labelId={id} label={label} value={value} onChange={(event) => onChange(event.target.value)}>
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
      {loading && <FormHelperText>Loading {plural}…</FormHelperText>}
      {failed && (
        <FormHelperText component="div">
          Could not load the {plural}.{' '}
          <Button size="small" onClick={retry} sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline' }}>
            Retry
          </Button>
        </FormHelperText>
      )}
    </FormControl>
  )
}

export default function CreateRequirementDialog({
  kind,
  pkg = '',
  onClose,
  onCreated,
}: {
  kind: KindSchema
  /**
   * The package it goes in, when somebody has already chosen one - the model
   * tree's New on a package row (issue #53). It WINS over the package the level
   * and scoping suggest (Patrick, 2026-09-28): they picked the place. The ID is
   * still suggested from the scoping.
   */
  pkg?: string
  onClose: () => void
  onCreated: (identifier: string) => void
}) {
  const schema = useSchema()
  const toasts = useToasts()

  const [typeName, setTypeName] = useState(kind.default_type)
  const [segment, setSegment] = useState('')
  const [subsystem, setSubsystem] = useState('')
  const [sources, setSources] = useState<string[]>([])
  const [statement, setStatement] = useState('')
  const [name, setName] = useState('')
  const [category, setCategory] = useState('')
  // Absent against a library that predates the attribute, and then not asked.
  const categoryField = kind.fields.find((entry) => entry.name === 'category') ?? null

  const [identifier, setIdentifier] = useState('')
  const [edited, setEdited] = useState(false)
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  /** Where it is written: the package chosen for it, else the suggested one. */
  const destination = pkg || (suggestion?.package ?? '')

  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const segmentField = kind.fields.find((entry) => entry.name === 'segment') ?? null
  const subsystemField = kind.fields.find((entry) => entry.name === 'subsystem') ?? null
  const segments = useChoices(kind.name, segmentField)
  const subsystems = useChoices(kind.name, subsystemField)

  // The level comes from the SCHEMA, not from the type's name. An abstract
  // requirement type - 'SubsystemMassRequirement' - has no digit in it, and the
  // server already knows its level because the loader resolves it the same way.
  // Reading '3' out of 'L3Requirement' here was a second implementation of that
  // rule, and it answered NaN for every declared type.
  const chosen = kind.types.find((entry) => entry.name === typeName) ?? NO_TYPE
  const level = chosen.level ?? NaN
  const axes = axesFor(chosen.level)
  // The narrowest axis is the one the ID and the package follow from.
  const axis = axes[axes.length - 1] ?? null

  // A typed requirement's statement is GENERATED from its values, so asking for
  // one would be asking for something the save overwrites.
  const generated = Boolean(chosen.template)

  // The controls this TYPE wants, resolved against the kind's own field list so
  // each is rendered by its KIND and not by its name.
  //
  // Two sources, and the second is not optional. `chosen.fields` is what the
  // type DECLARES - a mass, a comparison. The template also names features the
  // type INHERITED, and 'allocated_to' is the one every mass requirement needs:
  // left off the form, the requirement saves with the slot unfilled and its
  // statement reads "The {allocated_to} shall have a mass ...".
  const wanted = new Set([...chosen.fields, ...slotsIn(chosen.template)])
  const typeFields = kind.fields.filter(
    (entry) => !ASKED_ELSEWHERE.has(entry.name)
      && (wanted.has(entry.name) || (entry.feature && wanted.has(entry.feature))),
  )

  const [values, setValues] = useState<Record<string, unknown>>({})

  // Ask the server what this should be called and where it goes. Re-asked
  // whenever the answer would change, and never overwrites a typed ID.
  useEffect(() => {
    if (Number.isNaN(level)) return
    let live = true
    api
      .suggestRequirement({ level, segment, subsystem })
      .then((next) => {
        if (!live) return
        setSuggestion(next)
        if (!edited) setIdentifier(next.identifier)
      })
      .catch(() => live && setSuggestion(null))
    return () => {
      live = false
    }
  }, [level, segment, subsystem, edited])

  const scopeValue = axis === 'segment' ? segment : axis === 'subsystem' ? subsystem : ''
  const [makingPackage, setMakingPackage] = useState(false)

  // Typed words that have not been saved: leaving the page loses them.
  const release = useUnsavedGuard(Boolean(statement.trim() || name.trim() || sources.length))

  /**
   * Create the package this scoping has no home in yet.
   *
   * Placed inside the container the suggester named, which is the level's own
   * package - so a new subsystem lands beside its siblings rather than
   * wherever the last one happened to go.
   */
  const makePackage = async () => {
    if (!suggestion?.package || !scopeValue) return
    setMakingPackage(true)
    setFailure('')
    try {
      const created = await api.createPackage({
        parent: suggestion.package,
        name: titleCase(scopeValue),
        short_name: scopeValue.toUpperCase(),
      })
      toasts.succeeded(`Package ${created.name} created`)
      // Re-ask: the suggester will now offer the new package.
      const next = await api.suggestRequirement({ level, segment, subsystem })
      setSuggestion(next)
      if (!edited) setIdentifier(next.identifier)
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setMakingPackage(false)
    }
  }

  const submit = async () => {
    setBusy(true)
    setFailure('')
    setFieldErrors({})
    try {
      const body: Record<string, unknown> = {
        package: destination,
        identifier,
        type_name: typeName,
      }
      if (name) body.name = name
      // Not sent at all for a typed requirement: the back end renders the
      // statement from the values, and a statement in the body would be
      // written and then immediately replaced by the render.
      if (statement && !generated) body.statement = statement
      if (segment) body.segment = segment
      if (subsystem) body.subsystem = subsystem
      if (category.trim()) body.category = category.trim()
      for (const entry of typeFields) {
        const stated = values[entry.name]
        if (stated !== undefined && stated !== '') body[entry.name] = stated
      }

      const created = await api.create(kind.plural, body)

      // SOURCES ARE A SECOND CALL, and they have to be: a source is a SourceOf
      // CONNECTION written inside the requirement, so the requirement must
      // exist before one can be written into it. The create body cannot carry
      // them and never could.
      //
      // A failure here leaves a requirement that was created and a source that
      // was not, which is recoverable - the detail page states sources - so it
      // is reported plainly rather than rolled back. Rolling back would mean
      // deleting a requirement the user asked for because a second write
      // failed, which is the worse of the two.
      const refused: string[] = []
      for (const source of sources) {
        try {
          await api.addSource(created.identifier, source)
        } catch (error) {
          refused.push(`${source}: ${(error as ApiError).message}`)
        }
      }

      if (refused.length) {
        toasts.notify(
          `${created.identifier} was created, but ${refused.length === 1 ? 'a source was' : 'some sources were'} not: ` +
            `${refused.join('; ')}. Add ${refused.length === 1 ? 'it' : 'them'} on the requirement.`,
          'warning',
        )
      } else {
        toasts.succeeded(`${created.identifier} created`)
      }
      // Saved: nothing typed here is unsaved any more. Released BEFORE handing
      // off, because the list screens navigate to the new requirement in the
      // same tick, while this render still counts the form as dirty (#89).
      release()
      onCreated(created.identifier)
    } catch (error) {
      const failed = error as ApiError
      setFieldErrors(failed.fields ?? {})
      if (!Object.keys(failed.fields ?? {}).length) setFailure(failed.message)
    } finally {
      setBusy(false)
    }
  }

  // Every axis the level carries has to be answered. Without this the Create
  // button was live over a requirement BEST's own findings would then report as
  // having no segment.
  const scoped = axes.every((entry) => (entry === 'segment' ? segment : subsystem))
  const ready = Boolean(identifier && typeName && destination && scoped)

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New requirement</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}

          {/* 'Type', not 'Level'. A program's own requirement types are here
              beside the level chain now, and each carries the level it reaches
              - so this one control still decides the scoping, the ID and the
              package, exactly as the level alone used to. */}
          <FormControl size="small" required>
            <InputLabel id="new-type">Type</InputLabel>
            <Select
              labelId="new-type"
              label="Type"
              value={typeName}
              onChange={(event) => {
                setTypeName(event.target.value)
                // Values belong to the type that declared them. Kept across a
                // change, a mass typed for one type would be submitted for
                // another that never declared it and the create would fail on
                // a field the form no longer shows.
                setValues({})
              }}
            >
              {kind.types.map((choice) => (
                <MenuItem key={choice.name} value={choice.name}>
                  {choice.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {axes.includes('segment') && (
            <AxisSelect
              id="new-segment"
              label="Segment"
              plural="segments"
              value={segment}
              onChange={setSegment}
              choices={segments}
            />
          )}

          {axes.includes('subsystem') && (
            <AxisSelect
              id="new-subsystem"
              label="Subsystem"
              plural="subsystems"
              value={subsystem}
              onChange={setSubsystem}
              choices={subsystems}
            />
          )}

          <ReferencePicker
            schema={schema}
            target="requirement"
            label="Sources"
            helperText={
              fieldErrors.sources ||
              'The requirements this one flows down from. Several is normal, and no source is primary.'
            }
            error={Boolean(fieldErrors.sources)}
            multiple
            value={sources}
            onChange={(next) => setSources(Array.isArray(next) ? next : [])}
          />

          {/* The type's own attributes, each rendered by its FIELD KIND - a
              mass is a quantity control, a comparison is a menu - so declaring
              another one in the model costs nothing here. */}
          {typeFields.map((entry) => (
            <FieldControl
              key={entry.name}
              schema={schema}
              kind={kind}
              field={entry}
              value={values[entry.name] ?? ''}
              onChange={(next) => setValues((prior) => ({ ...prior, [entry.name]: next }))}
              error={fieldErrors[entry.name]}
            />
          ))}

          {generated ? (
            /* READ-ONLY, because it is output. The back end renders it from the
               values above on save and on every later load, so an editable box
               here would take an answer and discard it. */
            <TextField
              label="Statement"
              value={chosen.template}
              size="small"
              multiline
              minRows={2}
              disabled
              helperText="Generated from the values above, by this type's template"
            />
          ) : (
            <TextField
              label="Statement"
              value={statement}
              onChange={(event) => setStatement(event.target.value)}
              multiline
              minRows={2}
              size="small"
              error={Boolean(fieldErrors.statement)}
              helperText={fieldErrors.statement || 'The shall-statement itself'}
            />
          )}

          <TextField
            label="Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            size="small"
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name || 'Optional; defaults to the ID'}
          />

          {categoryField && (
            <FieldControl
              schema={schema}
              kind={kind}
              field={categoryField}
              value={category}
              onChange={(next) => setCategory(String(next ?? ''))}
              error={fieldErrors.category}
            />
          )}

          <Divider />

          {/* Last, because it FOLLOWS from everything above it. */}
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
              <TextField
                label="ID"
                required
                fullWidth
                value={identifier}
                onChange={(event) => {
                  setIdentifier(event.target.value)
                  setEdited(true)
                }}
                size="small"
                error={Boolean(fieldErrors.identifier)}
                helperText={fieldErrors.identifier}
                slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
              />
              {edited && suggestion && (
                <Button
                  size="small"
                  onClick={() => {
                    setIdentifier(suggestion.identifier)
                    setEdited(false)
                  }}
                  sx={{ mt: 0.25, flexShrink: 0 }}
                >
                  Use {suggestion.identifier}
                </Button>
              )}
            </Stack>

            {suggestion && (
              <Typography
                variant="caption"
                color={suggestion.source === 'guessed' ? 'warning.main' : 'text.secondary'}
                sx={{ display: 'block', mt: 0.5 }}
              >
                {suggestion.source === 'guessed' ? 'Guessed — ' : 'Following '}
                {suggestion.based_on}
              </Typography>
            )}
            {pkg ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', fontFamily: MONOSPACE, wordBreak: 'break-all' }}
              >
                In {pkg}
              </Typography>
            ) : suggestion?.package ? (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', fontFamily: MONOSPACE, wordBreak: 'break-all' }}
              >
                {suggestion.package}
              </Typography>
            ) : (
              <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                No package for this level and scoping yet.
              </Typography>
            )}

            {/* A new subsystem has no package, and until one exists the
                requirement has nowhere to go. Offering it here means the dead
                end is resolved where it is met. */}
            {!pkg && suggestion?.source === 'guessed' && scopeValue && (
              <Button
                size="small"
                sx={{ mt: 0.5 }}
                disabled={makingPackage}
                onClick={() => void makePackage()}
              >
                {makingPackage
                  ? 'Creating…'
                  : `Create a ${titleCase(scopeValue)} package for ${scopeValue}`}
              </Button>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!ready || busy}>
          {busy ? 'Creating…' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
